import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { authService, GoogleUser, AuthState } from '../services/auth';
import { logger } from '../utils/logger';

// =============================================================================
// TYPES
// =============================================================================

interface AuthContextType extends AuthState {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshAuth: () => void;
}

interface AuthAction {
  type: 'AUTH_START' | 'AUTH_SUCCESS' | 'AUTH_ERROR' | 'AUTH_LOGOUT' | 'AUTH_INIT';
  payload?: {
    user?: GoogleUser;
    error?: string;
  };
}

// =============================================================================
// REDUCER
// =============================================================================

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true, // Start with loading to check stored session
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_INIT':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: !!action.payload?.user,
        user: action.payload?.user || null,
        error: null,
      };

    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload?.user || null,
        error: null,
      };

    case 'AUTH_ERROR':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload?.error || 'Authentication failed',
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: null,
      };

    default:
      return state;
  }
}

// =============================================================================
// CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextType | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Initialize Google Auth Service
        await authService.initialize();

        // Check for existing session
        const storedSession = authService.getStoredSession();

        if (storedSession) {
          logger.debug('[AuthContext] Found stored session, auto-signing in');
          dispatch({
            type: 'AUTH_INIT',
            payload: {
              user: storedSession.user,
            },
          });
        } else {
          logger.debug('[AuthContext] No stored session found');
          dispatch({
            type: 'AUTH_INIT',
            payload: {},
          });

          // Try One Tap for returning users (web only)
          if (authService.isConfigured() && !authService.isNativePlatform()) {
            authService.promptOneTap(
              (user) => {
                dispatch({
                  type: 'AUTH_SUCCESS',
                  payload: { user },
                });
              },
              (error) => {
                // One Tap failed - this is normal, user will need to click sign-in
                logger.auth.debug('One Tap not available', error);
              }
            );
          }
        }
      } catch (error) {
        logger.auth.initFailed(error);
        dispatch({
          type: 'AUTH_INIT',
          payload: {},
        });
      }
    };

    initializeAuth();
  }, []);

  // =============================================================================
  // AUTH METHODS
  // =============================================================================

  const signIn = async (): Promise<void> => {
    logger.debug('[AuthContext] signIn called', {
      isConfigured: authService.isConfigured(),
      isNativePlatform: authService.isNativePlatform()
    });

    if (!authService.isConfigured()) {
      logger.error('[AuthContext] Google OAuth is not configured');
      dispatch({
        type: 'AUTH_ERROR',
        payload: { error: 'Google OAuth is not configured' },
      });
      return;
    }

    dispatch({ type: 'AUTH_START' });

    // Use native sign-in on Android/iOS
    if (authService.isNativePlatform()) {
      logger.debug('[AuthContext] Using native sign-in path');
      try {
        const user = await authService.nativeSignIn();
        logger.debug('[AuthContext] Native sign-in successful', { email: user.email });
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user },
        });
        return;
      } catch (error) {
        logger.error('[AuthContext] Native sign-in failed', error);
        const errorMessage = error instanceof Error ? error.message : 'Sign-in failed';
        dispatch({
          type: 'AUTH_ERROR',
          payload: { error: errorMessage },
        });
        // Re-throw so handleSignIn can catch and show alert
        throw error;
      }
    }

    // Web platform: use Google Identity Services
    return new Promise((resolve, reject) => {
      const buttonId = 'google-signin-button-temp';

      // Create temporary button element
      const tempButton = document.createElement('div');
      tempButton.id = buttonId;
      tempButton.style.position = 'absolute';
      tempButton.style.left = '-9999px';
      document.body.appendChild(tempButton);

      try {
        authService.initializeSignIn(
          buttonId,
          (user) => {
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user },
            });
            document.body.removeChild(tempButton);
            resolve();
          },
          (error) => {
            dispatch({
              type: 'AUTH_ERROR',
              payload: { error },
            });
            document.body.removeChild(tempButton);
            reject(new Error(error));
          }
        );

        // Automatically trigger the sign-in
        const button = tempButton.querySelector('div[role="button"]') as HTMLElement;
        if (button) {
          button.click();
        } else {
          // Fallback: trigger Google's sign-in popup directly
          if (window.google?.accounts?.id) {
            window.google.accounts.id.prompt();
          }
        }
      } catch (error) {
        document.body.removeChild(tempButton);
        dispatch({
          type: 'AUTH_ERROR',
          payload: { error: error instanceof Error ? error.message : 'Sign-in failed' },
        });
        reject(error);
      }
    });
  };

  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      logger.auth.debug('Sign out error', error);
      // Still clear local state even if Google sign-out fails
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const refreshAuth = (): void => {
    const storedSession = authService.getStoredSession();
    dispatch({
      type: 'AUTH_INIT',
      payload: {
        user: storedSession?.user || undefined,
      },
    });
  };

  // =============================================================================
  // CONTEXT VALUE
  // =============================================================================

  const contextValue: AuthContextType = {
    ...state,
    signIn,
    signOut,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};